import {
  CheckCircleFilled,
  CreditCardOutlined,
  LockFilled,
  SafetyCertificateFilled,
} from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import { setOtpVerifiedEmailAction } from 'common/commonRedux/auth/actions';
import { useAxiosRequest } from 'common/hooks';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface BillingModalProps {
  billingId: string | undefined;
  ammount: number | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  accountId?: string | number | undefined;
}

const formatAmount = (value: number | undefined) =>
  (value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const BillingModal: React.FC<BillingModalProps> = ({
  billingId,
  ammount,
  open,
  setOpen,
  accountId,
}) => {
  const dispatch: any = useDispatch();
  const createPaymentIntent = useAxiosRequest('');
  const displayAmount = formatAmount(ammount);
  const isSubmitting = createPaymentIntent.isLoading;
  const intAccountId = useSelector(
    (state: any) => state.auth?.profileData?.tokenData?.accountid
  );
  const handlePay = () => {
    if (!billingId || !ammount) {
      message.error('Missing billing information');
      return;
    }
    createPaymentIntent.apiAction({
      urlObjKey: 'CreatePaymentIntent',
      method: 'POST',
      skipAuth: true,
      headers: {
        'X-Api-Key': 'tf_stripe_chk_2f9c1a7e8b4d4c6aa1e0d7b5f3c2a90e',
        accept: 'text/plain',
        'Content-Type': 'application/json-patch+json',
      },
      payload: {
        billId: Number(billingId) || 0,
        accountId: Number(accountId || intAccountId) || 0,
      },
      cb: (res: any) => {
        const redirectUrl =
          res?.url || res?.checkoutUrl || res?.paymentUrl || res?.sessionUrl;
        if (redirectUrl) {
          message.success('Redirecting to Stripe...');
          window.location.href = redirectUrl;
        } else {
          message.success('Payment intent created.');
        }
        setOpen(false);
        dispatch(setOtpVerifiedEmailAction(''));
      },
      onError: (error: any) => {
        console.error('CreatePaymentIntent error:', error);
        message.error(
          error?.response?.data?.message ||
            error?.response?.data?.Message ||
            'Failed to initiate payment. Please try again.'
        );
      },
    });
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setOpen(false);
    dispatch(setOtpVerifiedEmailAction(''));
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={560}
      centered
      destroyOnClose
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      closable={!isSubmitting}
      zIndex={1200}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, overflow: 'hidden', borderRadius: 16 },
      }}
    >
      <div>
        {/* Order Summary */}
        <div className="relative bg-gradient-to-br from-primaryColor via-primaryColor to-blue-700 text-white p-7 overflow-hidden">
          <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-white/5 blur-2xl" />

          <div className="relative">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] opacity-90 mb-7">
              <CreditCardOutlined />
              <span>Secure checkout</span>
            </div>

            <div className="mb-7">
              <div className="text-xs uppercase tracking-wider opacity-80 mb-2">
                Amount due
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-md font-semibold opacity-90">USD</span>
                <span className="text-3xl font-bold leading-none">
                  ${displayAmount}
                </span>
              </div>
            </div>

            <div className="border-t border-white/20 pt-4 space-y-3 text-sm">
              {billingId && (
                <div className="flex items-center justify-between">
                  <span className="opacity-80">Reference</span>
                  <span className="font-mono font-semibold">#{billingId}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="opacity-80">Total</span>
                <span className="font-semibold">${displayAmount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust + Action panel */}
        <div className="p-6 md:p-7">
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-2.5 text-xs text-[#475467]">
              <SafetyCertificateFilled className="text-emerald-500 mt-0.5" />
              <div>
                <div className="font-semibold text-[#101828]">
                  256-bit SSL encryption
                </div>
                <div className="leading-relaxed">
                  Your details are transmitted securely.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 text-xs text-[#475467]">
              <LockFilled className="text-emerald-500 mt-0.5" />
              <div>
                <div className="font-semibold text-[#101828]">
                  PCI-compliant gateway
                </div>
                <div className="leading-relaxed">Processed by Stripe.</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 text-xs text-[#475467]">
              <CheckCircleFilled className="text-emerald-500 mt-0.5" />
              <div>
                <div className="font-semibold text-[#101828]">
                  No hidden fees
                </div>
                <div className="leading-relaxed">
                  The amount above is what you pay.
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 pt-5 border-t border-[#F2F4F7]">
            <Button
              size="large"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="min-w-[110px]"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handlePay}
              loading={isSubmitting}
              icon={!isSubmitting ? <LockFilled /> : undefined}
              className="bg-primaryColor min-w-[220px] !font-semibold"
              disabled={!billingId || !ammount}
            >
              {isSubmitting ? 'Processing…' : `Pay $${displayAmount} securely`}
            </Button>
          </div>

          <p className="text-xs text-[#98A2B3] text-center sm:text-right mt-4 leading-relaxed">
            By continuing, you agree to be redirected to Stripe to complete the
            transaction.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default BillingModal;
